/**
 * Standard height for AG Grid tables in the RPN/FPN sections.
 * Change this value to update all section grids at once.
 */
export const AGM_GRID_HEIGHT = '65vh';

/**
 * Single source of truth for RPN and FPN sections configuration.
 * This prevents mismatches between anchor navigation and container IDs.
 */

/**
 * Configuration for a single section in the RPN/FPN page
 */
export interface SectionConfig {
  id: string;
  title: string;
  level: 1 | 2 | 3;
  description?: string;
  componentKey?: string;
}

/**
 * Anchor navigation item for Cloudscape AnchorNavigation component
 */
export interface AnchorItem {
  text: string;
  href: string;
  level: 1 | 2 | 3;
}

/**
 * Common sections shared between RPN and FPN
 * These sections appear in both program types with identical configuration
 */
const COMMON_SECTIONS: readonly SectionConfig[] = [
  // Attributes Section
  {
    id: 'attributes',
    title: 'Attributes',
    level: 1,
    componentKey: 'attributes',
  },
  {
    id: 'standard-attributes',
    title: 'Standard Attributes',
    level: 2,
    componentKey: 'standardAttributes',
  },
  {
    id: 'product-line-attributes',
    title: 'Product Line Attributes',
    level: 2,
    componentKey: 'productLineAttributes',
  },
  {
    id: 'program-attributes',
    title: 'Program Attributes',
    level: 2,
    componentKey: 'programAttributes',
  },

  // Country & Key Dates Section
  {
    id: 'country-key-dates',
    title: 'Country & Key Dates',
    level: 1,
    componentKey: 'countryKeyDates',
  },
  {
    id: 'street-dates',
    title: 'Street Dates',
    level: 2,
    componentKey: 'streetDates',
  },
  {
    id: 'other-key-dates',
    title: 'Other Key Dates',
    level: 2,
    componentKey: 'otherKeyDates',
  },

  // Additional Info Section
  {
    id: 'additional-info',
    title: 'Additional Info',
    level: 1,
    componentKey: 'additionalInfo',
  },
  {
    id: 'manufacturing',
    title: 'Manufacturing',
    level: 2,
    componentKey: 'manufacturing',
  },
  {
    id: 'market',
    title: 'Market',
    level: 2,
    componentKey: 'market',
  },
  {
    id: 'specs',
    title: 'Specifications',
    level: 2,
    componentKey: 'specs',
  },
] as const;

const RPN_SPECIFIC_PREFIX: readonly SectionConfig[] = [
  // Overview Section
  {
    id: 'overview',
    title: 'Overview',
    level: 1,
    componentKey: 'overview',
  },
  {
    id: 'status',
    title: 'Program Status',
    level: 2,
    componentKey: 'status',
  },
  {
    id: 'program-overview',
    title: 'Program Overview',
    level: 2,
    componentKey: 'programOverview',
  },
  {
    id: 'documents-links',
    title: 'Additional Docs & Links',
    level: 2,
    componentKey: 'documentsLinks',
  },
  {
    id: 'contacts',
    title: 'Contacts',
    level: 2,
    componentKey: 'contacts',
  },
];

/**
 * RPN-specific sections that appear only in RPN programs
 */
const RPN_SPECIFIC_SECTIONS: readonly SectionConfig[] = [
  {
    id: 'agm',
    title: 'AGM',
    level: 1,
    componentKey: 'agm',
  },
  {
    id: 'versions',
    title: 'Versions',
    level: 2,
    componentKey: 'versions',
  },
  {
    id: 'assortments',
    title: 'Assortments',
    level: 2,
    componentKey: 'assortments',
  },
  {
    id: 'sku',
    title: 'SKU',
    level: 2,
    componentKey: 'sku',
  },
] as const;

/**
 * FPN-specific sections that appear at the beginning
 * These sections come BEFORE common sections to match RPN structure
 */
const FPN_SPECIFIC_PREFIX: readonly SectionConfig[] = [
  // Overview Section
  {
    id: 'overview',
    title: 'Overview',
    level: 1,
    componentKey: 'overview',
  },
  {
    id: 'program-overview',
    title: 'Program Overview',
    level: 2,
    componentKey: 'programOverview',
  },
];

/**
 * FPN-specific sections that appear after common sections
 * - agm & sku: AGM section with SKU (Read Only in FPN context)
 * Note: financial-info is inserted directly into FPN_SECTIONS (after additional-info)
 * to match the component rendering order.
 */
const FPN_SPECIFIC_SUFFIX: readonly SectionConfig[] = [
  // AGM Section - SKU is Read Only in FPN
  {
    id: 'agm',
    title: 'AGM',
    level: 1,
    componentKey: 'agm',
  },
  {
    id: 'sku',
    title: 'SKU',
    level: 2,
    componentKey: 'sku',
  },
] as const;

/**
 * FPN-specific: Financial Information section config.
 * Inserted right after 'additional-info' (level 1) in FPN_SECTIONS
 * so the anchor nav order matches the component rendering order.
 */
const FPN_FINANCIAL_INFO: SectionConfig = {
  id: 'financial-info',
  title: 'Financial Information',
  level: 2,
  componentKey: 'financialInfo',
};

/**
 * Complete RPN sections configuration
 * Combines common sections with RPN-specific sections
 */
export const RPN_SECTIONS: readonly SectionConfig[] = [
  ...RPN_SPECIFIC_PREFIX,
  ...COMMON_SECTIONS,
  ...RPN_SPECIFIC_SECTIONS,
] as const;

/**
 * Complete FPN sections configuration
 * Combines FPN prefix, common sections (with financial-info inserted after
 * additional-info), and FPN suffix.
 *
 * Financial Info is the first sub-section under "Additional Info" in FPN,
 * matching the component rendering order in AdditionalSections.tsx.
 */
export const FPN_SECTIONS: readonly SectionConfig[] = (() => {
  const base = [...FPN_SPECIFIC_PREFIX, ...COMMON_SECTIONS];
  // Insert financial-info right after additional-info (level 1)
  const additionalInfoIndex = base.findIndex((s) => s.id === 'additional-info');
  if (additionalInfoIndex !== -1) {
    base.splice(additionalInfoIndex + 1, 0, FPN_FINANCIAL_INFO);
  }
  return [...base, ...FPN_SPECIFIC_SUFFIX];
})() as readonly SectionConfig[];

/**
 * Generic helper to generate anchor navigation items from sections configuration
 *
 * @param sections - Array of section configurations
 * @returns Array of anchor items for Cloudscape AnchorNavigation component
 */
const createAnchorItems = (sections: readonly SectionConfig[]): AnchorItem[] => {
  return sections.map((section) => ({
    text: section.title,
    href: `#${section.id}`,
    level: section.level,
  }));
};

/**
 * Generate anchor navigation items for RPN sections.
 *
 * @returns Array of anchor items for RPN page navigation
 */
export const generateRpnAnchorItems = (): AnchorItem[] => {
  return createAnchorItems(RPN_SECTIONS);
};

/**
 * Generate anchor navigation items for FPN sections.
 *
 * @returns Array of anchor items for FPN page navigation
 */
export const generateFpnAnchorItems = (): AnchorItem[] => {
  return createAnchorItems(FPN_SECTIONS);
};

/**
 * Resolves a section hash from the current page to a valid hash on the target page type.
 * If the section exists on the target, returns it as-is.
 * If not, falls back to the parent level-1 section.
 * If no valid fallback, returns empty string (no hash).
 *
 * @param currentHash - Current URL hash (e.g., '#standard-attributes' or 'standard-attributes')
 * @param targetType - 'rpn' or 'fpn'
 * @returns Resolved hash string including '#' prefix, or ''
 */
export const resolveAnchorHashForTarget = (currentHash: string, targetType: 'rpn' | 'fpn'): string => {
  if (!currentHash) return '';

  const sectionId = currentHash.replace('#', '');
  const targetSections = targetType === 'rpn' ? RPN_SECTIONS : FPN_SECTIONS;
  const sourceSections = targetType === 'rpn' ? FPN_SECTIONS : RPN_SECTIONS;

  // If the target page has this exact section, keep it
  if (targetSections.some((s) => s.id === sectionId)) {
    return `#${sectionId}`;
  }

  // Find the parent level-1 section in the source config
  const sourceIndex = sourceSections.findIndex((s) => s.id === sectionId);
  if (sourceIndex === -1) return '';

  // Walk backwards from the source section to find its level-1 parent
  let parentId: string | undefined;
  for (let i = sourceIndex; i >= 0; i--) {
    if (sourceSections[i].level === 1) {
      parentId = sourceSections[i].id;
      break;
    }
  }

  // If the parent level-1 section exists on the target, use it
  if (parentId && targetSections.some((s) => s.id === parentId)) {
    return `#${parentId}`;
  }

  return '';
};
